import React, { useContext, useEffect, useState } from "react";
import { toArabicWord } from "number-to-arabic-words/dist/index-node.js";
import Image from "../../components/Image";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserAuth } from "../../utils/AuthContext";

const ReceiptTemplate = React.forwardRef((props, ref) => {
  const { t } = useTranslation("general");
  const { userData: UserData } = useContext(UserAuth);

  const {
    title = "فاتورة",
    headerText = "Market",
    headerImage,
    invoiceNumber,
    invoiceDate,
    items = [],
    subtotal,
    total,
    discount = 0,
    footerText = t("ehsiblySecondSlogan"),
    website = "www.alhabbar.com",
    userData = { username: "" },
    receivedAmount = 0,
    leftAmount = 0,
    columns = [
      { key: "name", label: t("details") },
      { key: "quantity", label: t("quantity") },
      { key: "price", label: t("price") },
      { key: "total", label: t("total") },
    ],
    showThankYou = true,
    showArabicTotal = true,
    time = new Date(),
    currency = {
      title: "iqd",
      value: "IQD",
    },
    measuringUnit = "ton",
  } = props;
  const [invoiceHeader, setInvoiceHeader] = useState(null);

  const fetchInvoiceTemplateImages = async (
    getInvoiceHeader = true,
    getInvoiceFooter = true,
    organizationId,
    subCompanyId = null
  ) => {
    try {
      const params = {
        getInvoiceHeader,
        getInvoiceFooter,
        organizationId: UserData.organizationId,
      };

      if (subCompanyId) {
        params.subCompanyId = subCompanyId;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getItemInvoiceTemplate`,
        {
          params,
        }
      );

      if (response.status == 200) {
        setInvoiceHeader(response.data.documents[0]);
      }
    } catch (error) {
      console.error("Error fetching invoice template images:", error);
      throw error;
    }
  };

  const calculateItemTotal = (item) => {
    if (item.quantity && item.price) {
      return item.quantity * item.price;
    }
    return item.total || 0;
  };

  const formatTime = (date) => {
    return date.getHours() + ":" + date.getMinutes();
  };

  const sumTotalWeight = (items) => {
    return items.reduce(
      (sum, item) =>
        sum +
        (isNaN(item.quantity) || !item.quantity ? 0 : Number(item.quantity)),
      0
    );
  };

  useEffect(() => {
    fetchInvoiceTemplateImages();
  }, []);

  return (
    <div style={{ display: "none" }}>
      <div className="receipt-container" ref={ref}>
        <div className="content-wrapper">
          <div className="receipt-header">
            {invoiceHeader && (
              <Image
                className="u-width_full-width"
                imagePath={invoiceHeader?.path}
                mode="show"
                imgClasses="u-width_full-width"
              />
            )}
            <div>{headerText}</div>
          </div>

          <div className="invoice-info">
            <div>
              {invoiceNumber && (
                <div>
                  {t("invoiceNumber")}: {invoiceNumber}
                </div>
              )}
              {userData?.username && (
                <div>
                  {t("seller")}: {userData.username}
                </div>
              )}
            </div>
            <div>
              {invoiceDate && (
                <div>
                  {t("Date")}: {invoiceDate}
                </div>
              )}
              <div>
                {t("time")}: {formatTime(time)}
              </div>
            </div>
          </div>

          <table className="receipt-table">
            <thead>
              <tr>
                {columns.map((col, index) => (
                  <th key={index} className="receipt-th">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items
                .filter((item) => item.name || item.price || item.quantity)
                .map((item, index) => (
                  <tr key={index}>
                    {columns.map((col, colIndex) => (
                      <td key={colIndex} className="receipt-td">
                        {col.key === "total"
                          ? calculateItemTotal(item) == 0
                            ? ""
                            : `${calculateItemTotal(item)} ${currency.value}`
                          : col.key === "price"
                          ? isNaN(item[col.key])
                            ? ""
                            : `${item[col.key]} ${currency.value}`
                          : col.key === "quantity"
                          ? isNaN(item[col.key])
                            ? ""
                            : `${item[col.key]} ${t(measuringUnit)}`
                          : item[col.key]}{" "}
                      </td>
                    ))}
                  </tr>
                ))}


              <tr>
                <td
                  colSpan={columns.length - 1}
                  className="receipt-td total-row"
                >
                  {t("totalWeight")}
                </td>
                <td className="receipt-td total-row">
                  {sumTotalWeight(items)} {t(measuringUnit)}
                </td>
              </tr>

              {subtotal !== undefined && (
                <tr>
                  <td
                    colSpan={columns.length - 1}
                    className="receipt-td total-row"
                  >
                    {t("totalPriceTag")}
                  </td>
                  <td className="receipt-td total-row">
                    {subtotal} {currency.value}
                  </td>
                </tr>
              )}

              {discount > 0 && (
                <tr>
                  <td colSpan={columns.length - 1} className="receipt-td">
                    {t("discount")}
                  </td>
                  <td className="receipt-td">
                    {discount} {t(currency.value)}
                  </td>
                </tr>
              )}
              {total !== undefined && (
                <tr>
                  <td
                    colSpan={columns.length - 1}
                    className="receipt-td total-row"
                  >
                    {t("netAmount")}
                  </td>
                  <td className="receipt-td total-row">
                    {parseInt(total || 0)} {currency.value}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="money-text">
            {showArabicTotal && total !== undefined && (
              <div>
                <span>{t("net")}: </span>
                {currency?.value == "IQD"
                  ? `${toArabicWord(total || 0)} ${t("iraqiDinar")} ${t(
                      "only"
                    )}`
                  : `${toArabicWord(total || 0)} ${t("usDollar")} ${t("only")}`}
              </div>
            )}
            <div className="o-column o-column--gap-size_200">
              <div>
                {t("received")}: {receivedAmount} {currency.value}
              </div>
              <div>
                {t("left")}: {leftAmount} {currency.value}
              </div>
            </div>
            <div className="o-column o-column--gap-size_200">
              {showArabicTotal && receivedAmount !== undefined && (
                <div>
                  {t("received")}:
                  {currency?.value == "IQD"
                    ? `${toArabicWord(receivedAmount || 0)} ${t(
                        "iraqiDinar"
                      )} ${t("only")}`
                    : `${toArabicWord(receivedAmount || 0)} ${t(
                        "usDollar"
                      )} ${t("only")}`}
                </div>
              )}
              {showArabicTotal && leftAmount !== undefined && (
                <div>
                  {t("left")}:
                  {currency?.value == "IQD"
                    ? `${toArabicWord(leftAmount || 0)} ${t("iraqiDinar")} ${t(
                        "only"
                      )}`
                    : `${toArabicWord(leftAmount || 0)} ${t("usDollar")} ${t(
                        "only"
                      )}`}
                </div>
              )}
            </div>
          </div>
        </div>

        {showThankYou && (
          <div className="center-text">{t("thanksForBuying")}</div>
        )}

        <div className="receipt-footer">
          <hr />
          <p>{t("ehsiblySecondSlogan")}</p>
          <span>{t("alhabbarSlogan")}</span>
        </div>
      </div>
    </div>
  );
});

export default ReceiptTemplate;
